import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboardIcon, ShieldIcon, TableIcon, BuildingIcon, PackageIcon, PercentIcon, UsersIcon, FileTextIcon, UploadCloudIcon, AlertCircleIcon, BellIcon, BarChart3Icon, ScrollTextIcon, ShieldCheckIcon, MenuIcon, XIcon, ChevronDownIcon, LogOutIcon, SearchIcon } from 'lucide-react';
import { DashboardModule } from '../components/admin/DashboardModule';
import { ProductCatalogueModule } from '../components/admin/ProductCatalogueModule';
import { RateTableModule } from '../components/admin/RateTableModule';
import { UnderwriterModule } from '../components/admin/UnderwriterModule';
import { BundleModule } from '../components/admin/BundleModule';
import { InterestRateModule } from '../components/admin/InterestRateModule';
import { UserManagementModule } from '../components/admin/UserManagementModule';
import { PolicyRegistryModule } from '../components/admin/PolicyRegistryModule';
import { CertificateModule } from '../components/admin/CertificateModule';
import { ClaimsModule } from '../components/admin/ClaimsModule';
import { NotificationModule } from '../components/admin/NotificationModule';
import { ReportingModule } from '../components/admin/ReportingModule';
import { AuditLogModule } from '../components/admin/AuditLogModule';
import { AdminUserModule } from '../components/admin/AdminUserModule';
export type AdminRole = 'Super Admin' | 'Ops Manager' | 'Claims Officer' | 'Support Agent';
export function AdminPanel() {
  const [role, setRole] = useState<AdminRole>('Super Admin');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const allModules = [{
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboardIcon,
    roles: ['Super Admin', 'Ops Manager', 'Claims Officer', 'Support Agent']
  }, {
    id: 'products',
    label: 'Product Catalogue',
    icon: ShieldIcon,
    roles: ['Super Admin', 'Ops Manager']
  }, {
    id: 'rates',
    label: 'Rate Tables',
    icon: TableIcon,
    roles: ['Super Admin', 'Ops Manager']
  }, {
    id: 'underwriters',
    label: 'Underwriters',
    icon: BuildingIcon,
    roles: ['Super Admin', 'Ops Manager']
  }, {
    id: 'bundles',
    label: 'Bundles',
    icon: PackageIcon,
    roles: ['Super Admin', 'Ops Manager']
  }, {
    id: 'interest',
    label: 'Interest Rates',
    icon: PercentIcon,
    roles: ['Super Admin', 'Ops Manager']
  }, {
    id: 'users',
    label: 'Users',
    icon: UsersIcon,
    roles: ['Super Admin', 'Ops Manager', 'Support Agent']
  }, {
    id: 'policies',
    label: 'Policy Registry',
    icon: FileTextIcon,
    roles: ['Super Admin', 'Ops Manager', 'Support Agent']
  }, {
    id: 'certificates',
    label: 'Certificates',
    icon: UploadCloudIcon,
    roles: ['Super Admin', 'Ops Manager', 'Support Agent']
  }, {
    id: 'claims',
    label: 'Claims',
    icon: AlertCircleIcon,
    roles: ['Super Admin', 'Claims Officer']
  }, {
    id: 'notifications',
    label: 'Notifications',
    icon: BellIcon,
    roles: ['Super Admin', 'Ops Manager']
  }, {
    id: 'reports',
    label: 'Reports',
    icon: BarChart3Icon,
    roles: ['Super Admin', 'Ops Manager']
  }, {
    id: 'audit',
    label: 'Audit Log',
    icon: ScrollTextIcon,
    roles: ['Super Admin']
  }, {
    id: 'admins',
    label: 'Admin Users',
    icon: ShieldCheckIcon,
    roles: ['Super Admin']
  }];
  const visibleModules = allModules.filter(m => m.roles.includes(role));
  useEffect(() => {
    if (!visibleModules.find(m => m.id === activeTab)) {
      setActiveTab('dashboard');
    }
  }, [role]);
  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardModule role={role} data-id="element-865" />;
      case 'products':
        return <ProductCatalogueModule role={role} data-id="element-866" />;
      case 'rates':
        return <RateTableModule role={role} data-id="element-867" />;
      case 'underwriters':
        return <UnderwriterModule role={role} data-id="element-868" />;
      case 'bundles':
        return <BundleModule role={role} data-id="element-869" />;
      case 'interest':
        return <InterestRateModule role={role} data-id="element-870" />;
      case 'users':
        return <UserManagementModule role={role} data-id="element-871" />;
      case 'policies':
        return <PolicyRegistryModule role={role} data-id="element-872" />;
      case 'certificates':
        return <CertificateModule role={role} data-id="element-873" />;
      case 'claims':
        return <ClaimsModule role={role} data-id="element-874" />;
      case 'notifications':
        return <NotificationModule role={role} data-id="element-875" />;
      case 'reports':
        return <ReportingModule role={role} data-id="element-876" />;
      case 'audit':
        return <AuditLogModule role={role} data-id="element-877" />;
      case 'admins':
        return <AdminUserModule role={role} data-id="element-878" />;
      default:
        return <DashboardModule role={role} data-id="element-879" />;
    }
  };
  return <div className="flex h-screen bg-slate-50 overflow-hidden font-sans" data-id="element-880">
      {/* Mobile Sidebar Overlay */}
      <AnimatePresence data-id="element-881">
        {sidebarOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setSidebarOpen(false)} className="fixed inset-0 bg-[#0D3B3B]/40 backdrop-blur-sm z-40 lg:hidden" data-id="element-882" />}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.div className={`fixed lg:static inset-y-0 left-0 w-72 bg-[#0D3B3B] text-white z-50 flex flex-col shadow-2xl lg:shadow-none transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`} data-id="element-883">
        <div className="p-6 flex items-center justify-between" data-id="element-884">
          <div className="flex items-center gap-3" data-id="element-885">
            <img src="https://cdn.magicpatterns.com/uploads/whJLzkvjNQX43Ru1gcfGZr/Group_581.png" alt="Skydd" className="h-8 w-auto" data-id="element-886" />
            <div className="h-8 w-px bg-white/20 mx-1" data-id="element-887"></div>
            <p className="text-[10px] text-emerald-400 font-bold uppercase tracking-widest leading-tight" data-id="element-888">
              Admin
              <br data-id="element-889" />
              Portal
            </p>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg" data-id="element-890">
            <XIcon className="w-5 h-5" data-id="element-891" />
          </button>
        </div>

        {/* Role Selector */}
        <div className="px-6 mb-6 relative" data-id="element-892">
          <button onClick={() => setRoleDropdownOpen(!roleDropdownOpen)} className="w-full bg-white/10 hover:bg-white/15 border border-white/10 rounded-xl p-3 flex items-center justify-between transition-colors" data-id="element-893">
            <div className="flex flex-col items-start" data-id="element-894">
              <span className="text-[10px] text-white/50 uppercase font-bold tracking-wider" data-id="element-895">
                Current Role
              </span>
              <span className="text-sm font-bold text-white" data-id="element-896">{role}</span>
            </div>
            <ChevronDownIcon className="w-4 h-4 text-white/50" data-id="element-897" />
          </button>

          <AnimatePresence data-id="element-898">
            {roleDropdownOpen && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="absolute top-full left-6 right-6 mt-2 bg-white rounded-xl shadow-xl overflow-hidden z-50 border border-gray-100" data-id="element-899">
                {['Super Admin', 'Ops Manager', 'Claims Officer', 'Support Agent'].map(r => <button key={r} onClick={() => {
              setRole(r as AdminRole);
              setRoleDropdownOpen(false);
            }} className={`w-full text-left px-4 py-3 text-sm font-bold transition-colors ${role === r ? 'bg-emerald-50 text-[#0D3B3B]' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-900">
                    {r}
                  </button>)}
              </motion.div>}
          </AnimatePresence>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-6 space-y-1 scrollbar-hide" data-id="element-901">
          {visibleModules.map(item => {
          const isActive = activeTab === item.id;
          return <button key={item.id} onClick={() => {
            setActiveTab(item.id);
            setSidebarOpen(false);
          }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${isActive ? 'bg-[#2BA89D] text-white font-bold shadow-lg shadow-teal-900/20' : 'text-white/60 hover:bg-white/5 hover:text-white font-medium'}`} data-id="element-902">
                <item.icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-white/50'}`} data-id="element-903" />
                <span className="text-sm" data-id="element-904">{item.label}</span>
              </button>;
        })}
        </div>

        <div className="p-4 border-t border-white/10" data-id="element-905">
          <button className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:bg-white/5 hover:text-rose-400 transition-colors font-medium" data-id="element-906">
            <LogOutIcon className="w-5 h-5" data-id="element-907" />
            <span className="text-sm" data-id="element-908">Sign Out</span>
          </button>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden" data-id="element-909">
        {/* Top Header */}
        <header className="bg-white border-b border-gray-100 h-16 flex items-center justify-between px-4 lg:px-8 shrink-0 z-10" data-id="element-910">
          <div className="flex items-center gap-4" data-id="element-911">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-gray-500 hover:bg-gray-50 rounded-lg" data-id="element-912">
              <MenuIcon className="w-6 h-6" data-id="element-913" />
            </button>
            <h1 className="text-xl font-bold text-[#0D3B3B] hidden sm:block" data-id="element-914">
              {allModules.find(m => m.id === activeTab)?.label}
            </h1>
          </div>
          <div className="flex items-center gap-4" data-id="element-915">
            <div className="relative" data-id="element-916">
              <SearchIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-917" />
              <input type="text" placeholder="Search anything..." className="pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] w-48 lg:w-64 transition-all" data-id="element-918" />
            </div>
            <button className="relative p-2 text-gray-400 hover:text-[#0D3B3B] transition-colors" data-id="element-919">
              <BellIcon className="w-6 h-6" data-id="element-920" />
              <span className="absolute top-1.5 right-2 w-2.5 h-2.5 bg-rose-500 rounded-full border-2 border-white" data-id="element-921"></span>
            </button>
            <div className="w-9 h-9 rounded-full bg-[#0D3B3B] text-white flex items-center justify-center font-bold text-sm shadow-sm" data-id="element-922">
              AD
            </div>
          </div>
        </header>

        {/* Module Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-8 relative" data-id="element-923">
          <AnimatePresence mode="wait" data-id="element-924">
            <motion.div key={activeTab} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.2
          }} className="h-full" data-id="element-925">
              {renderContent()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>;
}